import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(
  amount: number | string | null | undefined,
  currency: string = 'CAD',
  fractionDigits?: number
): string {
  if (amount === null || amount === undefined) return '—';
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '—';
  try {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency,
      ...(fractionDigits !== undefined
        ? { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }
        : {}),
    }).format(num);
  } catch {
    return `${num.toFixed(fractionDigits ?? 2)} ${currency}`;
  }
}

/**
 * Human-readable byte size, base-1024 (B, KB, MB, GB, TB, PB). Bytes render as an
 * integer ("512 B"); KB and larger render with one decimal ("1.5 KB", "1.0 GB").
 * Non-finite or negative input returns "0 B". Canonical replacement for the
 * per-feature copies that previously lived in hris/wms/brand/crm components.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const;
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return unit === 0 ? `${Math.round(value)} ${units[0]}` : `${value.toFixed(1)} ${units[unit]}`;
}

// ISO format everywhere: `yyyy-mm-dd` for dates, `yyyy-mm-dd hh:mm:ss` for
// datetimes. Locale `sv-SE` natively produces ISO 8601 (with a space separator
// between date and time), so we lean on Intl rather than hand-rolled padding.
// All datetimes are rendered in America/Montreal per the project default.

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '—';
  try {
    // Date-only strings (YYYY-MM-DD or ISO at midnight UTC) represent a calendar
    // day, not an instant. Return the YMD parts verbatim so the day does not
    // shift when displayed in America/Montreal (UTC-5).
    if (typeof date === 'string') {
      const m = date.match(/^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.000)?Z?)?$/);
      if (m) {
        const [, y, mo, d] = m;
        return `${y}-${mo}-${d}`;
      }
    }
    const parsed = new Date(date);
    if (isNaN(parsed.getTime())) return '—';
    return new Intl.DateTimeFormat('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'America/Montreal',
    }).format(parsed);
  } catch {
    return '—';
  }
}

export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'America/Montreal',
  }).format(parsed);
}

export function formatCompactCurrency(amount: number): string {
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toLocaleString()}`;
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}
